import React from 'react';
import Hyperlink from '@ajat/design-system/Hyperlink';
import { Mail, ExternalLink, ChevronRight, Download } from 'lucide-react';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../shared';

const VARIANTS = ['large', 'regular', 'small', 'editorial-large', 'editorial-regular', 'label'];

export const meta = {
  component: Hyperlink,
  title: 'Hyperlink',
  group: 'Actions',
  importPath: '@ajat/design-system/Hyperlink',
  figmaNode: '19:133',
  description:
    'Six variants across the UI and editorial families, each bound to its own typography class. Icons can be the built-in arrow pair or any lucide icon you pass in.',
  props: [
    ['children', 'node', '—', 'Link text (required)', ],
    ['href', 'string', "'#'", 'URL destination'],
    ['variant', "'large' | 'regular' | 'small' | 'editorial-large' | 'editorial-regular' | 'label'", "'regular'", 'Size and family — maps to the matching typo-link-* class'],
    ['iconLeft', 'node', 'null', 'Custom leading icon; overrides the default when showIconLeft is set'],
    ['iconRight', 'node', 'null', 'Custom trailing icon; overrides the default when showIconRight is set'],
    ['showIconLeft', 'bool', 'false', 'Show the default leading arrow'],
    ['showIconRight', 'bool', 'false', 'Show the default trailing arrow'],
    ['disabled', 'bool', 'false', 'Disables the link (gray-6, synced from Figma 19:133)'],
    ['inline', 'bool', 'false', 'A link inside running text — sits on the baseline instead of a flex row'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['onClick', 'func', '—', 'Click handler'],
  ],
  usage: [
    {
      title: 'Basic',
      code: `import Hyperlink from '@ajat/design-system/Hyperlink';

<Hyperlink variant="regular" href="/faq">Se ofte stillede spørgsmål</Hyperlink>

// Default arrow, no href — behaves as a button
<Hyperlink variant="small" showIconRight onClick={openPanel}>
  Størrelsesguide
</Hyperlink>`,
    },
    {
      title: 'Custom icons',
      code: `import { Mail, ExternalLink } from 'lucide-react';

<Hyperlink
  variant="regular"
  iconLeft={<Mail size={20} />}
  iconRight={<ExternalLink size={20} />}
  href="mailto:kontakt@example.com"
>
  Contact us
</Hyperlink>`,
    },
  ],
};

export default function HyperlinkPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="UI variants">
        <DemoRow align="center">
          <Demo caption="LARGE"><Hyperlink variant="large" showIconLeft showIconRight>Large Hyperlink</Hyperlink></Demo>
          <Demo caption="REGULAR"><Hyperlink variant="regular" showIconLeft showIconRight>Regular Hyperlink</Hyperlink></Demo>
          <Demo caption="SMALL"><Hyperlink variant="small" showIconLeft showIconRight>Small Hyperlink</Hyperlink></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Editorial variants">
        <DemoRow align="center">
          <Demo caption="EDITORIAL-LARGE"><Hyperlink variant="editorial-large" showIconLeft showIconRight>Editorial Large</Hyperlink></Demo>
          <Demo caption="EDITORIAL-REGULAR"><Hyperlink variant="editorial-regular" showIconLeft showIconRight>Editorial Regular</Hyperlink></Demo>
          <Demo caption="LABEL"><Hyperlink variant="label" showIconLeft showIconRight>Label Hyperlink</Hyperlink></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="States" hint="hover a link to see the hover transition">
        <DemoRow align="center">
          <Demo caption="DEFAULT"><Hyperlink variant="regular" showIconRight>Default</Hyperlink></Demo>
          <Demo caption="DISABLED"><Hyperlink variant="regular" showIconRight disabled>Disabled</Hyperlink></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Custom icons">
        <DemoRow align="center">
          <Hyperlink variant="regular" iconLeft={<Mail size={20} />} iconRight={<ExternalLink size={20} />}>
            Contact Us
          </Hyperlink>
          <Hyperlink variant="regular" iconRight={<ChevronRight size={20} />}>Learn More</Hyperlink>
          <Hyperlink variant="small" iconLeft={<Download size={20} />}>Download PDF</Hyperlink>
        </DemoRow>
      </DemoCard>

      <DemoCard title="All variants side by side" surface={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--space-ui-medium-s)]">
          {VARIANTS.map((variant) => (
            <div
              key={variant}
              className="flex items-center justify-between gap-[var(--space-ui-medium-s)] p-[var(--space-ui-small-l)] bg-[var(--color-gray-0)] rounded-[var(--radius-card)]"
            >
              <span className="text-[var(--ds-doc-ink-muted)]">{variant}</span>
              <Hyperlink variant={variant} showIconRight>Example Link</Hyperlink>
            </div>
          ))}
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
